import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Video, Play, Eye, Clock, Search, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { motion } from 'framer-motion';
import { API } from '../config/api';

export default function VideosPage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await fetch(`${API}/videos?limit=50`);
      if (response.ok) setVideos(await response.json());
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredVideos = search
    ? videos.filter(v =>
        v.title?.toLowerCase().includes(search.toLowerCase()) ||
        v.artist_name?.toLowerCase().includes(search.toLowerCase())
      )
    : videos;

  const handleVideoClick = (video) => {
    // Record play
    fetch(`${API}/videos/${video.video_id}/play`, { method: 'POST' }).catch(() => {});
    // Navigate to in-app video page
    navigate(`/video/${video.video_id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="videos-page">
      <div className="max-w-[1800px] mx-auto">
        <div className="mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase mb-2">Music Videos</h1>
            <p className="text-muted-foreground">Watch the latest music videos from DMV artists</p>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              data-testid="video-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search videos..."
              className="pl-9 bg-muted/50 border-border"
            />
          </div>
        </div>

        {filteredVideos.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredVideos.map((video, index) => (
              <motion.div
                key={video.video_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div
                  onClick={() => handleVideoClick(video)}
                  className="cursor-pointer"
                  data-testid={`video-card-${video.video_id}`}
                >
                  <Card className="glass-card border-border overflow-hidden hover-lift group">
                    <div className="aspect-video bg-muted relative">
                      {video.thumbnail_url ? (
                        <img src={video.thumbnail_url} alt={video.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                          <Video className="w-12 h-12 text-muted-foreground" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-background/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-14 h-14 rounded-full bg-muted/60 backdrop-blur-sm flex items-center justify-center">
                          <Play className="w-7 h-7 text-white ml-0.5" fill="white" />
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="font-medium truncate">{video.title}</p>
                      <p className="text-sm text-muted-foreground truncate">{video.artist_name || video.creator_name}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        {video.views_count > 0 && (
                          <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{video.views_count}</span>
                        )}
                      </div>
                    </div>
                  </Card>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <Video className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{search ? 'No videos matching your search' : 'No videos yet'}</p>
          </div>
        )}
      </div>
    </div>
  );
}
